import { FlexAlignCenterSpaceBetween } from 'components/primitives/flex';
import styled from 'styled-components';
import Button from 'components/ui/Button/Button';
import { useAtomCallback, useAtomValue } from 'jotai/utils';
import { useSetAtom, useAtom, atom } from 'jotai';
import FilterButton, { CustomFilterButtonProps } from '../FilterButton';
import {
  activitiesAtom,
  activitiesTempAtom,
  captainOptionsAtom,
  boatAmenitiesAtom,
  boatAmenitiesTempAtom,
  boatCategoriesTempAtom,
  boatCategoriesAtom,
  durationHoursAtom,
  durationHoursTempAtom,
  isAnyFilterActiveAtom,
  passengersCountAtom,
  passengersCountTempAtom,
  priceAtom,
  selectedPresetAtom,
  priceTempAtom,
  boatLengthAtom,
  boatLengthTempAtom,
  instantBookAtom,
  instantBookTempAtom,
  useResetGlobalFilters,
  selectedExperienceTagAtom,
  selectedExperienceTagTempAtom,
} from '../filterStore';
import OtherFiltersContent, { OtherFiltersLabel } from './OtherFiltersContent';
import { atomWithToggle } from '../atomUtils';
import { duplicateCaptainsAtom, duplicateSliderAtom, duplicateCheckboxAtomArrayAtom } from '../filterAtomUtils';

export const otherFiltersDropdownVisibleAtom = atomWithToggle(false);

export default function OtherFiltersFilterButton(props: CustomFilterButtonProps) {
  const { toggleFilterDropdown, dropdownActive } = props;
  const resetFilters = useResetGlobalFilters();
  const toggleFilterVisible = useSetAtom(otherFiltersDropdownVisibleAtom);
  const setSelectedPreset = useSetAtom(selectedPresetAtom);
  const [instantBookTempValue, toggleInstantBookTemp] = useAtom(instantBookAtom);
  const newInstantBookValue = useAtomValue(instantBookTempAtom);

  const handleToggle = (name) => {
    if (dropdownActive) {
      toggleFilterVisible(false);
    } else {
      toggleFilterVisible(true);
    }
    toggleFilterDropdown(name);
  };

  const updateAtoms = useAtomCallback((get, set) => {
    const newDurationsValueAtom = duplicateCheckboxAtomArrayAtom(durationHoursTempAtom);
    const newCaptainsValueAtom = duplicateCaptainsAtom(true);
    const newPriceAtom = duplicateSliderAtom(priceTempAtom);
    const newActivitiesValueAtom = duplicateCheckboxAtomArrayAtom(activitiesTempAtom);
    const newAmenitiesValueAtom = duplicateCheckboxAtomArrayAtom(boatAmenitiesTempAtom);
    const newBoatLengthAtom = duplicateSliderAtom(boatLengthTempAtom);
    const newBoatCategoriesAtom = duplicateCheckboxAtomArrayAtom(boatCategoriesTempAtom);
    const newExpTagAtom = atom(() => {
      const newVal = get(selectedExperienceTagTempAtom);
      if (typeof newVal === 'string' || !newVal) {
        return newVal;
      }
      return [...newVal];
    });

    if (instantBookTempValue !== newInstantBookValue) {
      toggleInstantBookTemp();
    }

    set(durationHoursAtom, get(newDurationsValueAtom));
    set(passengersCountAtom, get(passengersCountTempAtom));
    set(captainOptionsAtom, get(newCaptainsValueAtom));
    set(priceAtom, get(newPriceAtom));
    set(activitiesAtom, get(newActivitiesValueAtom));
    set(boatAmenitiesAtom, get(newAmenitiesValueAtom));
    set(boatLengthAtom, get(newBoatLengthAtom));
    set(boatCategoriesAtom, get(newBoatCategoriesAtom));
    set(selectedExperienceTagAtom, get(newExpTagAtom));
  });

  const showResultsClick = () => {
    toggleFilterVisible(false);
    toggleFilterDropdown(null);
    updateAtoms();
  };

  return (
    <FilterButton
      {...props}
      toggleFilterDropdown={handleToggle}
      hideCloseButton
      showCloseCross
      mobileFullscreen
      isFilterActiveAtom={isAnyFilterActiveAtom}
      label={<OtherFiltersLabel />}
      content={<OtherFiltersContent />}
      extraElementOutsideContent={
        <BottomBar>
          <ClearOtherFiltersButton
            onClick={() => {
              resetFilters();
              setSelectedPreset('');
            }}
          >
            Clear all
          </ClearOtherFiltersButton>
          <Button text="Show results" className="resultsBtn" onClick={showResultsClick} />
        </BottomBar>
      }
    />
  );
}

const BottomBar = styled(FlexAlignCenterSpaceBetween)`
  position: sticky;
  inset: 0 0 0 0;
  top: unset;
  background: #ffffff;
  box-shadow: 0px -4px 10px rgba(0, 0, 0, 0.04);
  padding: 20px 20px 24px;

  .resultsBtn {
    padding: 0 22px;
    background-color: #0751c2;
    text-transform: none;
  }
`;

const ClearOtherFiltersButton = styled.button`
  font-weight: 500;
  font-size: 15px;
  color: #17263c;
  padding: 18px 22px;
  text-decoration: underline;
`;
